import { useState, useEffect, useCallback } from 'react';
import {
  ConsentObject,
  readConsent,
  writeConsent,
  makeConsent,
  getConsentModeState,
} from '../../utils/cookieConsent';

/**
 * Manages the cookie consent state, persistence, and side effects.
 */
export const useCookieManager = () => {
  const [consent, setConsent] = useState<ConsentObject | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const initialConsent = readConsent();
    setConsent(initialConsent);

    if (initialConsent) {
      writeConsent(initialConsent);
      updateGoogleConsentMode(initialConsent);
    }
  }, []);

  const updateGoogleConsentMode = useCallback((currentConsent: ConsentObject | null) => {
    // TODO: Google Consent Mode v2 Integration
    // if (typeof window !== 'undefined' && window.gtag) {
    //   window.gtag('consent', 'update', getConsentModeState(currentConsent));
    // }
    console.log('[Cookie Consent] State updated:', getConsentModeState(currentConsent));
  }, []);

  const saveAndApply = useCallback(
    (newConsent: ConsentObject) => {
      setConsent(newConsent);
      writeConsent(newConsent);
      updateGoogleConsentMode(newConsent);
      setIsModalOpen(false);
    },
    [updateGoogleConsentMode],
  );

  const acceptAll = useCallback(() => {
    saveAndApply(makeConsent(true, true, true));
  }, [saveAndApply]);

  const rejectAll = useCallback(() => {
    saveAndApply(makeConsent(false, false, false));
  }, [saveAndApply]);

  const savePreferences = useCallback(
    (analytics: boolean, marketing: boolean, functional: boolean) => {
      saveAndApply(makeConsent(analytics, marketing, functional));
    },
    [saveAndApply],
  );

  const openSettings = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return {
    consent,
    isModalOpen,
    acceptAll,
    rejectAll,
    savePreferences,
    openSettings,
    closeSettings,
  };
};
